import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import type { MatchingResponse, Product, ProductList, UnmatchedPrices } from '../lib/types';
import { t } from '../i18n';
import { Button, Callout, Checkbox, Dialog, Empty, ErrorState, Field, Input, Ltr, Select, SkeletonRows, useDebounced, useToast } from '../components/ui';
import { IconLink, IconPlus, IconSearch, IconUpload } from '../components/icons';
import { MarginBadge } from '../components/Margin';

type Sort = 'name' | 'sku' | 'price' | 'sold' | 'margin';
const PRICE_SOURCE = { catalog: 'מקובץ הקטלוג', price_file: 'מקובץ המחירים', manual: 'נקבע ידנית' } as const;

export function ProductsPage() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'all' | 'no_price' | 'no_cost' | 'low_margin'>('active');
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [sort, setSort] = useState<Sort>('name');
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const dq = useDebounced(q);
  const list = useQuery({
    queryKey: ['products', dq, category, status, sort],
    queryFn: () => api<ProductList>(`/api/products${qs({ q: dq, category, status, sort })}`),
    placeholderData: (prev) => prev,
  });
  const matching = useQuery({ queryKey: ['product-matching', 'counts'], queryFn: () => api<MatchingResponse>('/api/products/matching?pageSize=10') });
  const unmatched = useQuery({ queryKey: ['products', 'unmatched-prices'], queryFn: () => api<UnmatchedPrices>('/api/products/prices/unmatched') });
  const data = list.data;
  // Before a catalog exists every description is "pending" — no point showing that number.
  const pending = matching.data?.catalogSize ? matching.data.counts.pending : 0;
  // Costs come only to users with the profit permission.
  const costs = Boolean(data?.profit);
  const cols = costs ? 10 : 8;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>קטלוג מוצרים</h1>
          <div className="sub">
            {data ? `${fmtNumber(data.counts.active)} מוצרים פעילים` : ' '} · המחירים לפני מע״מ. לכל מוצר — כמה פעמים הוצע ונמכר, במחיר האחרון ולמי.
          </div>
        </div>
        <div className="row wrap">
          <Link to="/products/matching" className="btn">
            <IconLink /> שיוך שורות למוצרים
            {pending ? <span className="badge warn" style={{ marginInlineStart: 4 }}>{fmtNumber(pending)}</span> : null}
          </Link>
          <Link to="/products/import" className="btn">
            <IconUpload /> ייבוא קטלוג
          </Link>
          <Link to="/products/prices" className="btn">
            <IconUpload /> ייבוא מחירים
          </Link>
          <Button variant="primary" onClick={() => setEditing('new')}>
            <IconPlus /> מוצר חדש
          </Button>
        </div>
      </div>

      {data && data.counts.total > 0 && data.counts.withoutPrice > 0 ? (
        <Callout
          tone="warn"
          action={status !== 'no_price' ? <Button size="sm" onClick={() => setStatus('no_price')}>הצגה</Button> : null}
        >
          {fmtNumber(data.counts.withoutPrice)} מוצרים בקטלוג עדיין בלי <strong>מחיר מכירה בסיסי</strong> — השם שלהם לא נמצא בקובץ המחירים{data.counts.priceFile ? '' : ' (עוד לא הועלה קובץ מחירים)'}. עלות לא מחליפה מחיר מכירה: מקלידים "מחיר בסיס" במוצר. ללקוח שכבר קנה את המוצר נשמר המחיר האחרון שלו בכל מקרה.
        </Callout>
      ) : null}
      {costs && data && data.counts.total > 0 && data.counts.withoutCost ? (
        <Callout tone="info" action={status !== 'no_cost' ? <Button size="sm" onClick={() => setStatus('no_cost')}>הצגה</Button> : null}>
          {fmtNumber(data.counts.withoutCost)} מוצרים פעילים בלי עלות — העלות מגיעה מעמודת "עלות" בקובץ המחירים, או נקבעת ידנית במוצר.
        </Callout>
      ) : null}
      {unmatched.data && unmatched.data.items.length > 0 && data && data.counts.total > 0 ? (
        <Callout tone="info" action={<Button size="sm" onClick={() => setShowUnmatched(true)}>רשימה</Button>}>
          {fmtNumber(unmatched.data.items.length)} שמות בקובץ המחירים לא נמצאו בקטלוג — כנראה שם שנכתב קצת אחרת. המחיר שלהם יחובר אוטומטית כשהשם יתאים.
        </Callout>
      ) : null}
      {data && data.counts.total === 0 ? (
        <div className="panel">
          <Empty
            title="הקטלוג ריק"
            body="מעלים שני קבצים, בכל סדר: קובץ קטלוג (מק״ט ושם מוצר) וקובץ מחירים (שם מוצר ומחיר). המחירים מתחברים למוצרים אוטומטית לפי השם. אחרי הייבוא המערכת מצליבה את המוצרים עם השורות בהצעות המחיר ובחשבוניות שכבר יצאו, וכך כל לקוח מקבל מחירון לפי מה שהוצע ונמכר לו."
            actions={
              <>
                <Link to="/products/import" className="btn primary"><IconUpload /> ייבוא קטלוג</Link>
                <Link to="/products/prices" className="btn"><IconUpload /> ייבוא מחירים</Link>
              </>
            }
          />
        </div>
      ) : (
        <section className="panel">
          <div className="panel-head row wrap" style={{ gap: 8 }}>
            <div className="row" style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
              <IconSearch style={{ position: 'absolute', insetInlineStart: 10, color: 'var(--faint)' }} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש לפי מק״ט או שם" style={{ paddingInlineStart: 32 }} aria-label="חיפוש" />
            </div>
            <Select value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 'auto' }} aria-label="קטגוריה">
              <option value="">כל הקטגוריות</option>
              {data?.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} style={{ width: 'auto' }} aria-label="סטטוס">
              <option value="active">פעילים</option>
              <option value="inactive">לא פעילים</option>
              <option value="all">הכל</option>
              <option value="no_price">בלי מחיר</option>
              {costs ? <option value="no_cost">בלי עלות</option> : null}
              {costs ? <option value="low_margin">רווחיות מתחת ליעד ({data?.profit?.target}%)</option> : null}
            </Select>
            <Select value={sort} onChange={(e) => setSort(e.target.value as Sort)} style={{ width: 'auto' }} aria-label="מיון">
              <option value="name">מיון: שם</option>
              <option value="sku">מיון: מק״ט</option>
              <option value="price">מיון: מחיר</option>
              <option value="sold">מיון: הכי נמכרים</option>
              {costs ? <option value="margin">מיון: רווחיות (נמוכה קודם)</option> : null}
            </Select>
            <span className="spacer" />
            {data ? <span className="small muted">{fmtNumber(data.items.length)} תוצאות</span> : null}
          </div>
          {list.error ? (
            <ErrorState error={list.error} onRetry={() => list.refetch()} />
          ) : (
            <div className="table-wrap" style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>מק״ט</th>
                    <th>מוצר</th>
                    <th className="hide-sm">קטגוריה</th>
                    <th className="num">מחיר בסיס</th>
                    {costs ? <th className="num">עלות</th> : null}
                    {costs ? <th className="num" title="רווח גולמי במחיר הבסיס: (מחיר − עלות) ÷ מחיר">רווחיות</th> : null}
                    <th className="num hide-sm">מחיר אחרון</th>
                    <th className="hide-sm">אחרון ל…</th>
                    <th className="num" title="שורות בהצעות, חשבוניות ותעודות">הופיע</th>
                    <th className="num hide-sm">לקוחות</th>
                  </tr>
                </thead>
                <tbody>
                  {!data ? (
                    <SkeletonRows rows={8} cols={cols} />
                  ) : data.items.length === 0 ? (
                    <tr><td colSpan={cols}><Empty title="לא נמצאו מוצרים" /></td></tr>
                  ) : (
                    data.items.map((p) => (
                      <tr key={p.id} className="clickable" onClick={() => setEditing(p)} style={p.active ? undefined : { opacity: 0.6 }}>
                        <td className="tabular"><Ltr>{p.sku}</Ltr></td>
                        <td className="primary">
                          {p.name}
                          {!p.active ? <span className="badge" style={{ marginInlineStart: 6 }}>לא פעיל</span> : null}
                          {p.description || p.unit || p.packSize ? <span className="sub">{[p.description, p.unit, p.packSize ? `${p.packSize} יח׳ בחבילה` : null].filter(Boolean).join(' · ')}</span> : null}
                        </td>
                        <td className="hide-sm small">{p.category ?? <span className="faint">—</span>}</td>
                        <td className="num tabular">{p.basePrice == null ? <span className="badge warn">אין מחיר</span> : fmtMoney(p.basePrice)}</td>
                        {costs ? (
                          <td className="num tabular" title={p.costFromPack ? 'עלות החבילה × חבילות בארגז' : undefined}>
                            {p.cost == null ? <span className="faint">—</span> : <>{fmtMoney(p.cost)}{p.costFromPack ? <span className="sub">מהחבילה</span> : null}</>}
                          </td>
                        ) : null}
                        {costs ? <td className="num"><MarginBadge margin={p.margin} level={p.marginLevel} /></td> : null}
                        <td className="num tabular hide-sm">
                          {p.history?.lastPrice != null ? (
                            <span style={p.basePrice != null && p.history.lastPrice < p.basePrice ? { color: 'var(--warn)' } : undefined}>{fmtMoney(p.history.lastPrice)}</span>
                          ) : <span className="faint">—</span>}
                        </td>
                        <td className="hide-sm small">
                          {p.history?.lastCustomer ? (
                            <>
                              <span className="ellipsis" style={{ maxWidth: 180, display: 'inline-block', verticalAlign: 'bottom' }}>{p.history.lastCustomer}</span>
                              <span className="sub">{p.history.lastType ? t.enums.docType[p.history.lastType] : ''} · {fmtDate(p.history.lastDate)}</span>
                            </>
                          ) : <span className="faint">—</span>}
                        </td>
                        <td className="num tabular">{p.history ? fmtNumber(p.history.lines) : <span className="faint">0</span>}</td>
                        <td className="num tabular hide-sm">{p.history ? fmtNumber(p.history.customers) : <span className="faint">0</span>}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <Dialog open={showUnmatched} onClose={() => setShowUnmatched(false)} title="שמות מקובץ המחירים שלא נמצאו בקטלוג" size="lg">
        <p className="small muted" style={{ marginTop: 0 }}>
          המחיר מתחבר למוצר כשהשם זהה (רווחים, גרשיים וסימנים לא משנים). אם השם בקטלוג כתוב אחרת — מתקנים באחד הקבצים ומעלים שוב, או מוסיפים מק״ט לקובץ המחירים.
        </p>
        <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table className="table">
            <thead><tr><th>שם בקובץ המחירים</th><th className="num">מחיר</th><th>הכי דומה בקטלוג</th></tr></thead>
            <tbody>
              {unmatched.data?.items.map((u) => (
                <tr key={u.name}>
                  <td className="primary">{u.name}{u.description || u.sku ? <span className="sub">{u.description}{u.description && u.sku ? ' · ' : ''}{u.sku ? <Ltr>{u.sku}</Ltr> : null}</span> : null}</td>
                  <td className="num tabular">{fmtMoney(u.price)}</td>
                  <td className="small">{u.suggestion ? <>{u.suggestion.name} <span className="faint">({Math.round(u.suggestion.score * 100)}%)</span></> : <span className="faint">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Dialog>

      {editing ? <ProductDialog product={editing === 'new' ? null : editing} categories={data?.categories ?? []} showCost={costs} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function ProductDialog({ product, categories, showCost, onClose }: { product: Product | null; categories: string[]; showCost: boolean; onClose: () => void }) {
  const [form, setForm] = useState({
    sku: product?.sku ?? '',
    name: product?.name ?? '',
    price: product?.basePrice != null ? String(product.basePrice) : '',
    // A carton's cost taken from its pack is not its own — the field stays empty.
    cost: product?.cost != null && !product.costFromPack ? String(product.cost) : '',
    category: product?.category ?? '',
    unit: product?.unit ?? '',
    unitsPerPack: product?.unitsPerPack != null ? String(product.unitsPerPack) : '',
    description: product?.description ?? '',
    active: product?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  useEffect(() => setErr(null), [form]);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    const price = form.price.trim() === '' ? null : Number(form.price.replace(/[₪,\s]/g, ''));
    if (!form.sku.trim() || !form.name.trim()) return setErr('מק״ט ושם מוצר הם שדות חובה');
    if (price != null && (!Number.isFinite(price) || price < 0)) return setErr('מחיר לא תקין');
    const cost = form.cost.trim() === '' ? null : Number(form.cost.replace(/[₪,\s]/g, ''));
    if (cost != null && (!Number.isFinite(cost) || cost <= 0)) return setErr('עלות לא תקינה');
    setBusy(true);
    try {
      const packs = form.unitsPerPack.trim() === '' ? null : Number(form.unitsPerPack);
      if (packs != null && !(Number.isInteger(packs) && packs >= 1)) return setErr('יחידות בחבילה — מספר שלם');
      const body = { sku: form.sku.trim(), name: form.name.trim(), price, category: form.category.trim() || null, unit: form.unit.trim() || null, unitsPerPack: packs, description: form.description.trim() || null, active: form.active, ...(showCost && cost != null ? { cost } : {}) };
      await api(product ? `/api/products/${product.id}` : '/api/products', { method: product ? 'PATCH' : 'POST', body });
      for (const k of ['products', 'product-matching', 'price-list']) qc.invalidateQueries({ queryKey: [k] });
      toast.success(product ? 'המוצר עודכן' : 'המוצר נוסף לקטלוג');
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={product ? product.name : 'מוצר חדש'}
      footer={
        <>
          <Button variant="primary" onClick={save} loading={busy}>{t.common.save}</Button>
          <Button onClick={onClose}>{t.common.cancel}</Button>
        </>
      }
    >
      <div className="stack">
        {err ? <Callout tone="bad">{err}</Callout> : null}
        <div className="form-grid">
          <Field label="מק״ט" required><Input value={form.sku} onChange={set('sku')} ltr /></Field>
          <Field
            label="מחיר בסיס (לפני מע״מ)"
            hint={product?.basePriceSource ? `${PRICE_SOURCE[product.basePriceSource]}${product.basePriceUpdatedAt ? ` · ${fmtDate(product.basePriceUpdatedAt)}` : ''}` : 'ריק = יגיע מקובץ המחירים'}
          >
            <Input value={form.price} onChange={set('price')} inputMode="decimal" ltr />
          </Field>
        </div>
        {showCost ? (
          <div className="form-grid">
            <Field
              label="עלות (לפני מע״מ)"
              hint={
                product?.costFromPack
                  ? `ריק = עלות החבילה × חבילות (${fmtMoney(product.cost ?? 0)})`
                  : product?.costUpdatedAt
                    ? `עודכנה ${fmtDate(product.costUpdatedAt)} · שינוי נשמר מהיום, הקודמת נשמרת בהיסטוריה`
                    : 'בדרך כלל מגיעה מעמודת "עלות" בקובץ המחירים'
              }
            >
              <Input value={form.cost} onChange={set('cost')} inputMode="decimal" ltr />
            </Field>
            <Field label="רווחיות במחיר הבסיס">
              <div style={{ paddingTop: 6 }}>{marginPreview(form.price, form.cost || (product?.costFromPack ? String(product.cost) : ''))}</div>
            </Field>
          </div>
        ) : null}
        <Field label="שם המוצר" required hint="כך יופיע בשורת המסמך במורנינג"><Input value={form.name} onChange={set('name')} /></Field>
        <div className="form-grid">
          <Field label="קטגוריה">
            <Input value={form.category} onChange={set('category')} list="product-categories" />
            <datalist id="product-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="יחידה"><Input value={form.unit} onChange={set('unit')} placeholder="יח׳, ארגז, חבילה…" /></Field>
          <Field
            label="יחידות בחבילה"
            hint={form.unitsPerPack ? 'לקריאת חשבוניות שתמחרו יחידה בודדת' : product?.packSize ? `ריק = ${product.packSize} (נקרא מהשם)` : 'לא חובה — למשל 48 עטים בכוס'}
          >
            <Input value={form.unitsPerPack} onChange={set('unitsPerPack')} inputMode="numeric" ltr />
          </Field>
        </div>
        <Field label="תיאור"><Input value={form.description} onChange={set('description')} /></Field>
        {product ? <Checkbox checked={form.active} onChange={(v) => setForm({ ...form, active: v })}>מוצר פעיל (מופיע במחירונים)</Checkbox> : null}
        {product?.history ? (
          <div className="small muted">
            הופיע ב-{fmtNumber(product.history.lines)} שורות ({fmtNumber(product.history.soldLines)} מכירות) אצל {fmtNumber(product.history.customers)} לקוחות.
            {product.history.lastPrice != null ? ` מחיר אחרון ${fmtMoney(product.history.lastPrice)} ל${product.history.lastCustomer ?? '—'} ב-${fmtDate(product.history.lastDate)}.` : ''}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Margin while typing: (price − cost) ÷ price. */
function marginPreview(priceText: string, costText: string) {
  const price = Number(priceText.replace(/[₪,\s]/g, ''));
  const cost = Number(costText.replace(/[₪,\s]/g, ''));
  if (!priceText.trim() || !costText.trim() || !(price > 0) || !(cost > 0)) return <span className="faint">—</span>;
  const m = Math.round(((price - cost) / price) * 1000) / 10;
  return (
    <span className="tabular">
      <b>{m.toLocaleString('he-IL')}%</b> · רווח {fmtMoney(price - cost)} ליחידת מכירה
    </span>
  );
}
